import { isLiveDashboardChart } from './liveDashboardDefaults'
import {
  buildGroupedHierarchy,
  createNameMatcher,
  flattenDimensionAttributes,
  normalizeDisplayName,
  normalizeId,
  parseAttributeLevel,
  parseGroupedSelectionToUiState,
  resolveGroupedAttributeIds,
  stripParentIdsWhenChildrenPresent,
  stripTotalPrefix,
} from './liveDashboardDimensionUtils'

export const LIVE_DASHBOARD_ASSET_CLASS_GROUPS = [
  {
    assetClass: 'Passive',
    subAssetClasses: [
      'Passive Equity',
      'Passive Fixed Income',
      'Passive TDRFs / TRFs',
      'Rest of Passive Multi-Asset',
      'Passive Other',
    ],
  },
  {
    assetClass: 'Active Equity',
    subAssetClasses: [
      'Quantitative Active Equity',
      'Fundamental Active Domestic Large Cap Equity',
      'Fundamental Active Domestic Mid / Small Cap Equity',
      'Fundamental Active Domestic Other Equity',
      'Fundamental Active Global Equity',
      'Fundamental Active International Equity',
      'Fundamental Active Emerging Market Equity',
    ],
  },
  {
    assetClass: 'Active Fixed Income',
    subAssetClasses: [
      'Domestic Core / Core Plus',
      'Global / International Fixed Income',
      'Emerging Market Debt',
      'High Yield',
      'Leveraged Loans',
      'Tax-Exempt',
      'Short Duration',
      'Long Duration',
      'Unconstrained / Flexible Fixed Income',
    ],
  },
  {
    assetClass: 'Active Multi-Asset',
    subAssetClasses: [
      'Active GTAA / TAA',
      'Active TDRFs / TRFs',
      'Active Balanced / Other Multi-Asset',
    ],
  },
  {
    assetClass: 'Total Alternatives',
    subAssetClasses: [
      'Real Estate',
      'Structured Products',
      'Hedge Funds',
      'Private Equity',
      'Infrastructure',
      'Private Debt',
      'Commodities',
      'Absolute Return / Liquid Alternatives',
      'Alternatives-Undefined',
    ],
  },
  {
    assetClass: 'Total Money Market',
    subAssetClasses: ['Total Money Market'],
  },
]

export const LIVE_DASHBOARD_ASSET_CLASS_NAMES = LIVE_DASHBOARD_ASSET_CLASS_GROUPS.map(
  (group) => group.assetClass,
)

export const LIVE_DASHBOARD_SUB_ASSET_CLASS_NAMES = LIVE_DASHBOARD_ASSET_CLASS_GROUPS.flatMap(
  (group) => group.subAssetClasses,
)

const assetClassNameMatcher = createNameMatcher({
  matchDisplayName: (displayName, allowedName) => {
    const normalized = normalizeDisplayName(displayName)
    const allowedNormalized = normalizeDisplayName(allowedName)
    return normalized === allowedNormalized || stripTotalPrefix(normalized) === allowedNormalized
  },
})

const {
  findMatchingAllowedName,
  findAttributeByAllowedName,
  findDashboardParentAttribute,
} = assetClassNameMatcher

/** Prefer deepest metadata match; exclude L1 parent when a distinct sub-asset class exists. */
const findChildAttributeByAllowedName = (flatAttributes, allowedName, allowedNames, parentAttribute) => {
  const matches = flatAttributes.filter(
    (attribute) => findMatchingAllowedName(attribute.DisplayName, [allowedName]) != null,
  )

  if (!matches.length) {
    return null
  }

  const parentId = normalizeId(parentAttribute?.AttributeID)
  const distinctMatches = matches.filter(
    (attribute) => normalizeId(attribute.AttributeID) !== parentId,
  )
  const candidates = distinctMatches.length > 0 ? distinctMatches : matches

  return candidates.reduce((best, candidate) => {
    const bestLevel = parseAttributeLevel(best) ?? Number.MIN_SAFE_INTEGER
    const candidateLevel = parseAttributeLevel(candidate) ?? Number.MIN_SAFE_INTEGER
    return candidateLevel > bestLevel ? candidate : best
  })
}

const dedupeSubAssetClasses = (subAssetClasses, parentAttribute) =>
  subAssetClasses.filter(
    (subAssetClass, index, items) =>
      items.findIndex((entry) => normalizeId(entry.id) === normalizeId(subAssetClass.id)) === index
      && (
        items.length === 1
        || normalizeId(subAssetClass.id) !== normalizeId(parentAttribute.AttributeID)
      ),
  )

const buildHierarchyFromMetadata = (dimension) => {
  const flatAttributes = flattenDimensionAttributes(dimension?.Attributes)
  if (!flatAttributes.length) {
    return { assetClassesWithSubAssetClasses: [], assetClassOnlyNodes: [] }
  }

  const { groupsWithChildren, leafOnlyGroups } = buildGroupedHierarchy(
    flatAttributes,
    LIVE_DASHBOARD_ASSET_CLASS_GROUPS,
    {
      getParentName: (group) => group.assetClass,
      getChildNames: (group) => group.subAssetClasses,
      allChildNames: LIVE_DASHBOARD_SUB_ASSET_CLASS_NAMES,
      findDashboardParentAttribute,
      findAttributeByAllowedName,
      findChildAttributeByAllowedName,
      dedupeChildren: dedupeSubAssetClasses,
    },
  )

  return {
    assetClassesWithSubAssetClasses: groupsWithChildren.map((group) => ({
      id: group.id,
      name: group.name,
      subAssetClasses: group.children,
    })),
    assetClassOnlyNodes: leafOnlyGroups,
  }
}

export const findDefaultAssetClassAttributeId = (dimension, preferredAssetClassName = 'Passive') => {
  const hierarchy = buildAssetClassHierarchy(dimension)

  const preferredAssetClass = [...hierarchy.assetClassesWithSubAssetClasses, ...hierarchy.assetClassOnlyNodes]
    .find((assetClass) =>
      findMatchingAllowedName(assetClass.name, [preferredAssetClassName]) != null,
    )

  return preferredAssetClass?.id
    ?? hierarchy.assetClassesWithSubAssetClasses[0]?.id
    ?? hierarchy.assetClassOnlyNodes[0]?.id
    ?? null
}

export const findAllDefaultAssetClassAttributeIds = (dimension) => {
  const hierarchy = buildAssetClassHierarchy(dimension)
  const selectedAssetClassIds = [
    ...hierarchy.assetClassesWithSubAssetClasses.map((assetClass) => assetClass.id),
    ...hierarchy.assetClassOnlyNodes.map((assetClass) => assetClass.id),
  ]
  const selectedSubAssetClassIds = hierarchy.assetClassesWithSubAssetClasses.flatMap((assetClass) =>
    assetClass.subAssetClasses.map((subAssetClass) => subAssetClass.id),
  )

  return resolveAssetClassAttributeIds(
    selectedAssetClassIds,
    selectedSubAssetClassIds,
    hierarchy,
  )
}

export function buildAssetClassHierarchy(dimension) {
  return buildHierarchyFromMetadata(dimension)
}

export function isAssetClassDimension(dimension) {
  const name = String(dimension?.Name ?? '').toLowerCase()
  const display = String(dimension?.DisplayName ?? '').toLowerCase()
  return name === 'assetclassid' || display.includes('asset class')
}

export function isLiveDashboardAssetClassDimension(chart, dimension) {
  return isLiveDashboardChart(chart) && isAssetClassDimension(dimension)
}

export function parseAssetClassSelectionToUiState(attributeIds, hierarchy) {
  const uiState = parseGroupedSelectionToUiState(attributeIds, hierarchy, {
    groupsWithChildren: hierarchy.assetClassesWithSubAssetClasses.map((assetClass) => ({
      id: assetClass.id,
      children: assetClass.subAssetClasses,
    })),
    leafOnlyGroups: hierarchy.assetClassOnlyNodes,
  })

  return {
    selectedAssetClassIds: uiState.selectedParentIds,
    selectedSubAssetClassIds: uiState.selectedChildIds,
  }
}

export function getAssetClassSelectionDisplayCount(dimension, attributeIds) {
  const hierarchy = buildAssetClassHierarchy(dimension)
  const uiState = parseAssetClassSelectionToUiState(attributeIds, hierarchy)

  let count = uiState.selectedSubAssetClassIds.length

  for (const node of hierarchy.assetClassOnlyNodes) {
    if (uiState.selectedAssetClassIds.some((id) => normalizeId(id) === normalizeId(node.id))) {
      count += 1
    }
  }

  return count || (attributeIds?.length ?? 0)
}

export function isAssetClassTotalSelection(assetClass, selectedSubAssetClassIds) {
  const subAssetClasses = assetClass?.subAssetClasses ?? assetClass?.children ?? []
  if (subAssetClasses.length === 0) {
    return true
  }

  const selectedSubAssetClasses = new Set((selectedSubAssetClassIds ?? []).map(normalizeId))
  const selectedCount = subAssetClasses.filter((subAssetClass) =>
    selectedSubAssetClasses.has(normalizeId(subAssetClass.id)),
  ).length

  // L1 total only when no sub-asset classes are explicitly selected (matches geography:
  // region with zero countries uses parent ID; all countries selected keeps child IDs).
  return selectedCount === 0
}

/** For API/chart queries: collapse full sub-asset class totals to L1 parent IDs. */
export function isAssetClassApiTotalSelection(assetClass, selectedSubAssetClassIds) {
  const subAssetClasses = assetClass?.subAssetClasses ?? assetClass?.children ?? []
  if (subAssetClasses.length === 0) {
    return true
  }

  const selectedSubAssetClasses = new Set((selectedSubAssetClassIds ?? []).map(normalizeId))
  const selectedCount = subAssetClasses.filter((subAssetClass) =>
    selectedSubAssetClasses.has(normalizeId(subAssetClass.id)),
  ).length

  return selectedCount === 0 || selectedCount === subAssetClasses.length
}

export function resolveAssetClassAttributeIds(
  selectedAssetClassIds,
  selectedSubAssetClassIds,
  hierarchy,
) {
  return resolveGroupedAttributeIds(
    selectedAssetClassIds,
    selectedSubAssetClassIds,
    hierarchy,
    {
      groupsWithChildren: hierarchy.assetClassesWithSubAssetClasses.map((assetClass) => ({
        id: assetClass.id,
        children: assetClass.subAssetClasses,
      })),
      leafOnlyGroups: hierarchy.assetClassOnlyNodes,
      isTotalSelection: isAssetClassTotalSelection,
      useParentIdForTotal: true,
    },
  )
}

export function resolveAssetClassApiAttributeIds(
  selectedAssetClassIds,
  selectedSubAssetClassIds,
  hierarchy,
) {
  return resolveGroupedAttributeIds(
    selectedAssetClassIds,
    selectedSubAssetClassIds,
    hierarchy,
    {
      groupsWithChildren: hierarchy.assetClassesWithSubAssetClasses.map((assetClass) => ({
        id: assetClass.id,
        children: assetClass.subAssetClasses,
      })),
      leafOnlyGroups: hierarchy.assetClassOnlyNodes,
      isTotalSelection: isAssetClassApiTotalSelection,
      useParentIdForTotal: true,
    },
  )
}

export function resolveAssetClassDisplayAttributeIds(
  selectedAssetClassIds,
  selectedSubAssetClassIds,
  hierarchy,
) {
  return resolveGroupedAttributeIds(
    selectedAssetClassIds,
    selectedSubAssetClassIds,
    hierarchy,
    {
      groupsWithChildren: hierarchy.assetClassesWithSubAssetClasses.map((assetClass) => ({
        id: assetClass.id,
        children: assetClass.subAssetClasses,
      })),
      leafOnlyGroups: hierarchy.assetClassOnlyNodes,
    },
  )
}

export function applyLiveDashboardAssetClassResolutionToSelections(selections, dimensions) {
  if (!Array.isArray(selections) || !Array.isArray(dimensions)) {
    return selections
  }

  return selections.map((selection) => {
    const dimension = dimensions.find((entry) => entry.DimensionID === selection.DimensionID)
    if (!dimension || !isAssetClassDimension(dimension)) {
      return selection
    }

    const hierarchy = buildAssetClassHierarchy(dimension)
    const uiState = parseAssetClassSelectionToUiState(selection.Attributes, hierarchy)
    const resolvedAttributes = resolveAssetClassApiAttributeIds(
      uiState.selectedAssetClassIds,
      uiState.selectedSubAssetClassIds,
      hierarchy,
    )

    return {
      ...selection,
      Attributes: resolvedAttributes,
    }
  })
}

export function applyLiveDashboardAssetClassDisplayResolutionToSelections(selections, dimensions) {
  if (!Array.isArray(selections) || !Array.isArray(dimensions)) {
    return selections
  }

  return selections.map((selection) => {
    const dimension = dimensions.find((entry) => entry.DimensionID === selection.DimensionID)
    if (!dimension || !isAssetClassDimension(dimension)) {
      return selection
    }

    const hierarchy = buildAssetClassHierarchy(dimension)
    const uiState = parseAssetClassSelectionToUiState(selection.Attributes, hierarchy)
    const resolvedAttributes = stripParentIdsWhenChildrenPresent(
      resolveAssetClassDisplayAttributeIds(
        uiState.selectedAssetClassIds,
        uiState.selectedSubAssetClassIds,
        hierarchy,
      ),
      hierarchy.assetClassesWithSubAssetClasses.map((assetClass) => ({
        id: assetClass.id,
        children: assetClass.subAssetClasses,
      })),
    )

    return {
      ...selection,
      Attributes: resolvedAttributes,
    }
  })
}
